import {
  Autocomplete,
  Chip,
  DialogContent,
  Grid,
  Checkbox,
  FormControl,
} from "@mui/material";
import { DateRangePicker } from "rsuite";
import { Formik } from "formik";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import moment from "moment-timezone";
import { useMaterialUIController } from "context";
import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CircularIndeterminate from "components/MDLoading";
import { assignRates } from "store/slice/rates/rateSlice";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { getAssignRatesByPlaceId } from "store/slice/rates/rateSlice";
import { getCalenderDataByPlaceId } from "store/slice/rates/rateSlice";
import { getAssignRatesTableDataByPlaceId } from "store/slice/rates/rateSlice";
import CustomTooltip from "components/Common/CustomTooltip";
import * as Yup from "yup";
import { updatePlace, getPlaces } from "store/slice/places/placeSlice";
import { triggerSetupSnackbar } from "store/slice/globalControl/globalControlSlice";

const tabInfo = {
  isVipParking:
    "Assign separate rate plans for reserved or VIP spaces. Enables space selection and overrides occupancy.",
  isUnitParking:
    "Enables per-unit/reserved allocations and disables hourly-only rates.",
  isExtensionRate:
    "Mark this rate as an additional charge applicable after the booked period.",
  isExtensionBasedRate:
    "If enabled, this extension's price is calculated from the linked primary rate",
  isHideFromSuggestions:
    "Keep this rate out of quick-select suggestion chips used in UI.",
};

const isValidObjectId = (id) =>
  typeof id === "string" && /^[a-fA-F0-9]{24}$/.test(id);

const AssignRatesToPlace = (props) => {
  const formikRef = useRef(null);
  const { dialogOpen, onClose, placeId, ratesData, occupancy, spacesData } =
    props;
  const dispatch = useDispatch();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const [isLoading, setIsLoading] = useState(false);
  const [openTooltipId, setOpenTooltipId] = useState(null);
  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });
  const placeData = useSelector((state) => state.places?.selectedPlace || {});

  const handleAssignSuccess = () => {
    // Fire guide event — GuideOverlay checks transit/active state, so it's safe to always dispatch
    window.dispatchEvent(
      new CustomEvent("GUIDE_STEP_COMPLETE", {
        detail: { step: "ASSIGN_RATE" },
      }),
    );
  };

  const handleAssignRates = async (rateData) => {
    try {
      setIsLoading(true);
      const res = await dispatch(assignRates(rateData)).unwrap();
      const success = res?.success;
      setNotification({
        ...notification,
        color: success ? "success" : "error",
        title: success ? "Success" : "Error",
        content: res?.message || "An unexpected error occurred.",
        icon: success ? "check" : "warning",
        show: true,
      });
      if (success) {
        const currentSetup = placeData?.setup || {};
        const currentSteps = currentSetup?.steps || {};
        const payload = {
          setup: {
            ...currentSetup,
            steps: {
              ...currentSteps,
              ratesAssigned: true,
            },
          },
        };
        await dispatch(getAssignRatesByPlaceId(placeId));
        await dispatch(getCalenderDataByPlaceId(placeId));
        await dispatch(getAssignRatesTableDataByPlaceId(placeId));
        await dispatch(
          updatePlace({
            placeId,
            type: "UPDATE",
            ...payload,
          }),
        ).unwrap();
        // ✅ Refresh placesList + selectedPlace so checklist shows ratesAssigned:true
        await dispatch(getPlaces());
        onClose();
        handleAssignSuccess();
        // Use saved payload.setup (not stale placeData.setup)
        dispatch(triggerSetupSnackbar(payload?.setup));
      }
    } catch (err) {
      console.error("Error assigning rate:", err);
      setNotification({
        ...notification,
        color: "error",
        title: "Error",
        content: err?.message || "An error occurred while assigning rates.",
        icon: "warning",
        show: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const Days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "All",
  ];

  const occupancyOption = Array.from(
    { length: occupancy },
    (_, index) => index + 1,
  ).map((option) => ({
    value: option,
    label: `${option} (${Math.round((option / occupancy) * 100)}%)`,
  }));

  // Helper flags derived directly from place settings
  const isPlaceVip = !!placeData?.isVipParking;
  const isPlaceUnit = !!placeData?.isUnitParkingFlow;

  const validationSchema = Yup.object().shape({
    rateIds: Yup.array()
      .min(1, "At least one rate must be selected")
      .required("Rate is required"),
    days: Yup.array()
      .min(1, "At least one day must be selected")
      .required("Days are required"),
    startTime: Yup.date().nullable().required("Start time is required"),
    endTime: Yup.date().nullable().required("End time is required"),
    ...(placeData?.isPercentageOccupancyFlow
      ? {
          minOccupancy: Yup.number()
            .typeError("Min occupancy must be a number")
            .min(0, "Min occupancy cannot be less than 0%")
            .max(100, "Min occupancy cannot exceed 100%")
            .required("Min occupancy is required"),
          maxOccupancy: Yup.number()
            .typeError("Max occupancy must be a number")
            .min(0, "Max occupancy cannot be less than 0%")
            .max(100, "Max occupancy cannot exceed 100%")
            .required("Max occupancy is required")
            .moreThan(
              Yup.ref("minOccupancy"),
              "Max occupancy must be greater than Min occupancy",
            ),
        }
      : {
          occupancy: Yup.number().required("Occupancy is required"),
        }),
  });

  return (
    <>
      <CircularIndeterminate
        type="full"
        size={20}
        text={"Please wait..!"}
        open={isLoading}
      />
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
      <MDDialog
        dialogTitle="Assign Rates"
        open={dialogOpen}
        dialogClose={onClose}
        closeIcon={true}
        maxWidth="sm"
      >
        <DialogContent>
          <Formik
            innerRef={formikRef}
            validationSchema={validationSchema}
            initialValues={{
              placeId: null,
              rateIds: [],
              startTime: new Date(new Date().setHours(0, 0, 0)),
              endTime: new Date(new Date().setHours(23, 59, 59)),
              days: [],
              placeObj: null,
              rateArray: [],
              isExtensionRate: false,
              extendedFor: "",
              isExtensionBasedRate: false,
              isHideFromSuggestions: false,
              minEndTime: "",
              occupancy: occupancy,
              minOccupancy: 0,
              maxOccupancy: 100,
              isVipParking: isPlaceVip,
              isUnitParking: isPlaceUnit,
              spaceIds: [],
              spaceArray: [],
            }}
            onSubmit={(value) => {
              const payload = {
                placeId,
                rateIds: (value?.rateIds || []).filter(isValidObjectId),
                spaceIds: (value?.spaceIds || [])
                  .filter((id) => id !== "all")
                  .filter(isValidObjectId),
                startTime: moment(value?.startTime).format("HH:mm"),
                endTime: moment(value?.endTime).format("HH:mm"),
                days: value?.days,
                isExtensionRate: value?.isExtensionRate,
                isVipParking: value?.isVipParking,
                isUnitParking: value?.isUnitParking,
                isExtensionBasedRate: value?.isExtensionBasedRate,
                isHideFromSuggestions: value?.isHideFromSuggestions,
                occupancy: value?.occupancy,
                minOccupancy: value?.minOccupancy,
                maxOccupancy: value?.maxOccupancy,
              };
              if (
                value?.isExtensionBasedRate &&
                isValidObjectId(value?.extendedFor)
              ) {
                payload.extendedFor = value.extendedFor;
              }
              handleAssignRates(payload);
            }}
          >
            {(props) => {
              // Combine Place settings with Form values for robust UI logic
              const isVipSelected = isPlaceVip || props.values.isVipParking;
              const isUnitSelected = isPlaceUnit || props.values.isUnitParking;
              // Auto clear extension if VIP or Unit forced
              if (isVipSelected || isUnitSelected) {
                if (props.values.isExtensionRate) {
                  props.setFieldValue("isExtensionRate", false);
                  props.setFieldValue("isExtensionBasedRate", false);
                  props.setFieldValue("extendedFor", "");
                }
              }

              return (
                <form onSubmit={props.handleSubmit}>
                  <Grid container spacing={2} className="mt-1">
                    {props.values.isExtensionRate &&
                      props.values.isExtensionBasedRate && (
                        <Grid item xs={12}>
                          <Autocomplete
                            options={ratesData.filter(
                              (rate) => rate.status === 10,
                            )}
                            getOptionLabel={(option) =>
                              `${option.displayName} ${
                                option.title ? `(${option.title})` : ""
                              }`
                            }
                            renderOption={(props, option) => (
                              <li {...props}>
                                <div>{option.displayName}</div>
                              </li>
                            )}
                            name="extendedFor"
                            autoHighlight
                            disableClearable
                            size="small"
                            value={
                              ratesData.find(
                                (option) =>
                                  option._id === props.values.extendedFor,
                              ) || null
                            }
                            getOptionSelected={(option, value) =>
                              option._id === value._id
                            }
                            onChange={(event, value) => {
                              props.setFieldValue(
                                "extendedFor",
                                value ? value._id : "",
                              );
                            }}
                            renderInput={(params) => (
                              <MDInput
                                label="Extended Rate"
                                fullWidth
                                inputProps={{
                                  ...params.inputProps,
                                  autoComplete: "new-password",
                                }}
                                value={
                                  props.values.extendedFor
                                    ? ratesData.find(
                                        (option) =>
                                          option._id ===
                                          props.values.extendedFor,
                                      )?.title
                                    : ""
                                }
                                {...params}
                              />
                            )}
                          />
                        </Grid>
                      )}
                    <Grid item xs={12} md={12} lg={12}>
                      <Autocomplete
                        multiple
                        disableClearable
                        disablePortal
                        blurOnSelect="touch"
                        name="rateId"
                        value={props.values?.rateArray || []}
                        isOptionEqualToValue={(option, value) =>
                          option._id === value._id
                        }
                        options={ratesData.filter((rate) => {
                          if (rate.status !== 10) return false;

                          if (isUnitSelected) {
                            return (
                              rate.isRateOption !== true &&
                              rate.rateType !== "hourly" &&
                              rate.rateType !== "monthly"
                            );
                          }
                          return true;
                        })}
                        onChange={(event, newValues) => {
                          props.setFieldValue(
                            "rateIds",
                            newValues.map(({ _id }) => _id),
                          );
                          props.setFieldValue("rateArray", newValues);
                        }}
                        onBlur={props.handleBlur}
                        getOptionLabel={(option) =>
                          `${option.displayName} ${
                            option.title ? `(${option.title})` : ""
                          }`
                        }
                        renderInput={(params) => (
                          <MDInput
                            label="Rate"
                            fullWidth
                            error={
                              props.touched.rateIds && !!props.errors.rateIds
                            }
                            helperText={
                              props.touched.rateIds && props.errors.rateIds
                            }
                            {...params}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              key={option?._id}
                              variant="contained"
                              label={option?.displayName}
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                      />
                    </Grid>
                    {(isVipSelected || isUnitSelected) && (
                      <Grid item xs={12} md={12} lg={12}>
                        <Autocomplete
                          multiple
                          disableClearable
                          name="spaceIds"
                          value={props.values?.spaceArray}
                          options={[
                            { _id: "all", title: "Select All" },
                            ...spacesData.filter((space) => space.isPrimary),
                          ]}
                          onChange={(event, newValues) => {
                            const isAllSelected = newValues.some(
                              (value) => value._id === "all",
                            );
                            if (isAllSelected) {
                              const allSpaces = spacesData.filter(
                                (space) => space.isPrimary,
                              );
                              props.setValues({
                                ...props?.values,
                                spaceIds: allSpaces.map(({ _id }) => _id),
                                spaceArray: allSpaces,
                              });
                            } else {
                              props.setValues({
                                ...props?.values,
                                spaceIds: newValues
                                  .filter((v) => v._id !== "all")
                                  .map(({ _id }) => _id),
                                spaceArray: newValues.filter(
                                  (v) => v._id !== "all",
                                ),
                              });
                            }
                          }}
                          onBlur={props.handleBlur}
                          getOptionLabel={(option) => option?.title}
                          renderInput={(params) => (
                            <MDInput
                              label="Select Space"
                              fullWidth={true}
                              {...params}
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                key={index}
                                variant="contained"
                                label={option?.title}
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={12} lg={12}>
                      {placeData?.isPercentageOccupancyFlow ? (
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <MDInput
                              type="number"
                              label="Min Occupancy (%)"
                              name="minOccupancy"
                              value={props.values.minOccupancy}
                              onChange={props.handleChange}
                              onBlur={props.handleBlur}
                              error={
                                props.touched.minOccupancy &&
                                !!props.errors.minOccupancy
                              }
                              helperText={
                                props.touched.minOccupancy &&
                                props.errors.minOccupancy
                              }
                              fullWidth
                              disabled={isVipSelected || isUnitSelected}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <MDInput
                              type="number"
                              label="Max Occupancy (%)"
                              name="maxOccupancy"
                              value={props.values.maxOccupancy}
                              onChange={props.handleChange}
                              onBlur={props.handleBlur}
                              error={
                                props.touched.maxOccupancy &&
                                !!props.errors.maxOccupancy
                              }
                              helperText={
                                props.touched.maxOccupancy &&
                                props.errors.maxOccupancy
                              }
                              fullWidth
                              disabled={isVipSelected || isUnitSelected}
                            />
                          </Grid>
                        </Grid>
                      ) : (
                        <Autocomplete
                          disableClearable
                          name="occupancy"
                          value={
                            props.values.occupancy
                              ? occupancyOption.find(
                                  (option) =>
                                    option.value === props.values.occupancy,
                                )
                              : null
                          }
                          options={occupancyOption}
                          onChange={(event, newValue) => {
                            props.setFieldValue("occupancy", newValue?.value);
                          }}
                          onBlur={props.handleBlur}
                          renderInput={(params) => (
                            <MDInput
                              label="Occupancy"
                              fullWidth={true}
                              error={
                                props.touched.occupancy &&
                                !!props.errors.occupancy
                              }
                              helperText={
                                props.touched.occupancy &&
                                props.errors.occupancy
                              }
                              {...params}
                            />
                          )}
                          disabled={isVipSelected || isUnitSelected}
                          getOptionLabel={(option) => option.label}
                          renderOption={(props, option) => (
                            <li {...props}>{option.label}</li>
                          )}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <DateRangePicker
                          id="timeDuration"
                          placeholder="Start Time - End Time"
                          character=" - "
                          size="lg"
                          format="hh:mm a"
                          showMeridian
                          value={
                            props.values.startTime && props.values.endTime
                              ? [
                                  new Date(props.values.startTime),
                                  new Date(props.values.endTime),
                                ]
                              : [
                                  new Date(new Date().setHours(0, 0, 0)),
                                  new Date(new Date().setHours(23, 59, 59)),
                                ]
                          }
                          ranges={[]}
                          onChange={(value) => {
                            if (value && value.length === 2) {
                              if (
                                isNaN(Date.parse(value[0])) ||
                                isNaN(Date.parse(value[1]))
                              ) {
                                props.setFieldValue(
                                  "startTime",
                                  new Date(new Date().setHours(0, 0, 0)),
                                );
                                props.setFieldValue(
                                  "endTime",
                                  new Date(new Date().setHours(23, 59, 59)),
                                );
                              } else {
                                props.setFieldValue("startTime", value[0]);
                                props.setFieldValue("endTime", value[1]);
                              }
                            } else {
                              props.setFieldValue("startTime", null);
                              props.setFieldValue("endTime", null);
                            }
                          }}
                          onBlur={props.handleBlur}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}>
                      <Autocomplete
                        multiple
                        disableClearable
                        name="days"
                        value={props.values?.days || []}
                        options={Days.filter(
                          (option) => !props.values?.days?.includes(option),
                        )}
                        onChange={(event, newValues) => {
                          props.setValues({
                            ...props?.values,
                            days:
                              newValues.indexOf("All") === -1
                                ? newValues
                                : [
                                    "Sunday",
                                    "Monday",
                                    "Tuesday",
                                    "Wednesday",
                                    "Thursday",
                                    "Friday",
                                    "Saturday",
                                  ],
                          });
                        }}
                        onBlur={props.handleBlur}
                        getOptionLabel={(option) => option}
                        renderInput={(params) => (
                          <MDInput
                            label="Days"
                            fullWidth={true}
                            error={props.touched.days && !!props.errors.days}
                            helperText={props.touched.days && props.errors.days}
                            {...params}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              key={index}
                              variant="contained"
                              label={option}
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                      />
                    </Grid>

                    {/* --- UPDATED CHECKBOX / MESSAGE ROWS --- */}

                    {/* If VIP is forced by Place settings, show Message. Else show Checkbox (unless Unit forced) */}
                    {
                      isPlaceVip ? (
                        <Grid item xs={12}>
                          <MDBox
                            bgColor={sidenavColor}
                            borderRadius="lg"
                            p={2}
                            display="flex"
                            alignItems="center"
                            coloredShadow={sidenavColor}
                          >
                            <MDTypography
                              variant="button"
                              color="white"
                              fontWeight="medium"
                            >
                              VIP Parking Enabled
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="white"
                              sx={{ ml: 2, opacity: 0.8 }}
                            >
                              (Configured in Places Settings)
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      ) : null
                      // !isPlaceUnit && (
                      //   <Grid item xs={12} md={6}>
                      //     <MDBox display="flex" alignItems="center" ml={-1}>
                      //       <Checkbox
                      //         id="isVipParking"
                      //         name="isVipParking"
                      //         checked={props.values.isVipParking}
                      //         onChange={(e) => {
                      //           const isChecked = e.target.checked;
                      //           props.setFieldValue("isVipParking", isChecked);
                      //           props.setFieldValue("isUnitParking", false);
                      //           props.setFieldValue(
                      //             "spaceIds",
                      //             isChecked ? props.values.spaceIds : [],
                      //           );
                      //           props.setFieldValue(
                      //             "spaceArray",
                      //             isChecked ? props.values.spaceArray : [],
                      //           );
                      //           props.setFieldValue(
                      //             "occupancy",
                      //             isChecked ? occupancy : props.values.occupancy,
                      //           );
                      //           if (isChecked) {
                      //             props.setFieldValue("isExtensionRate", false);
                      //             props.setFieldValue("isExtensionBasedRate", false);
                      //             props.setFieldValue("extendedFor", "");
                      //           }
                      //         }}
                      //         inputProps={{ "aria-label": "isVipParking" }}
                      //       />
                      //       <MDTypography
                      //         variant="button"
                      //         fontWeight="regular"
                      //         color="text"
                      //       >
                      //         Is VIP Parking
                      //       </MDTypography>
                      //       <CustomTooltip
                      //         id="isVipParking"
                      //         title={tabInfo.isVipParking}
                      //         openTooltipId={openTooltipId}
                      //         setOpenTooltipId={setOpenTooltipId}
                      //         iconSize={16}
                      //       />
                      //     </MDBox>
                      //   </Grid>
                      // )
                    }

                    {/* If Unit is forced by Place settings, show Message. Else show Checkbox (unless VIP forced) */}
                    {/* {isPlaceUnit ? (
                     <Grid item xs={12}>
                      <MDBox
                        bgColor="info"
                        borderRadius="lg"
                        p={2}
                        display="flex"
                        alignItems="center"
                        coloredShadow="info"
                      >
                         <MDTypography variant="button" color="white" fontWeight="medium">
                            Unit Parking Enabled
                         </MDTypography>
                         <MDTypography variant="caption" color="white" sx={{ ml: 2, opacity: 0.8 }}>
                            (Configured in Places Settings)
                         </MDTypography>
                      </MDBox>
                    </Grid>
                  ) : null  */}
                    {!isPlaceVip && isPlaceUnit && (
                      <Grid item xs={12} md={6}>
                        <MDBox display="flex" alignItems="center" ml={-1}>
                          <Checkbox
                            id="isUnitParking"
                            name="isUnitParking"
                            checked={props.values.isUnitParking}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              props.setFieldValue("isUnitParking", isChecked);
                              props.setFieldValue("isVipParking", false);
                              props.setFieldValue(
                                "spaceIds",
                                isChecked ? props.values.spaceIds : [],
                              );
                              props.setFieldValue(
                                "spaceArray",
                                isChecked ? props.values.spaceArray : [],
                              );
                              props.setFieldValue(
                                "occupancy",
                                isChecked ? occupancy : props.values.occupancy,
                              );
                              if (isChecked) {
                                props.setFieldValue("isExtensionRate", false);
                                props.setFieldValue(
                                  "isExtensionBasedRate",
                                  false,
                                );
                                props.setFieldValue("extendedFor", "");
                                // Clear selected rates if they are hourly
                                if (
                                  props.values.rateArray.some(
                                    (rate) =>
                                      rate.rateType === "hourly" ||
                                      rate.rateType === "monthly" ||
                                      rate.isRateOption === true,
                                  )
                                ) {
                                  props.setFieldValue("rateIds", []);
                                  props.setFieldValue("rateArray", []);
                                }
                              }
                            }}
                            inputProps={{ "aria-label": "isUnitParking" }}
                          />
                          <MDTypography
                            variant="button"
                            fontWeight="regular"
                            color="text"
                          >
                            Is Unit Parking
                          </MDTypography>

                          <CustomTooltip
                            id="isUnitParking"
                            title={tabInfo.isUnitParking}
                            openTooltipId={openTooltipId}
                            setOpenTooltipId={setOpenTooltipId}
                            iconSize={16}
                          />
                        </MDBox>
                      </Grid>
                    )}

                    {/* Extension Rates: Completely hidden if VIP or Unit mode is active */}
                    {!isVipSelected && !isUnitSelected && (
                      <Grid item xs={12} md={6}>
                        <MDBox display="flex" alignItems="center" ml={-1}>
                          <Checkbox
                            id="isExtensionRate"
                            name="isExtensionRate"
                            checked={props.values.isExtensionRate}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              props.setFieldValue("isExtensionRate", isChecked);
                              if (!isChecked) {
                                props.setFieldValue(
                                  "isExtensionBasedRate",
                                  false,
                                );
                                props.setFieldValue("extendedFor", "");
                              }
                            }}
                            inputProps={{ "aria-label": "isExtensionRate" }}
                          />
                          <MDTypography
                            variant="button"
                            fontWeight="regular"
                            color="text"
                          >
                            Is this extension rate?
                          </MDTypography>

                          <CustomTooltip
                            id="isExtensionRate"
                            title={tabInfo.isExtensionRate}
                            openTooltipId={openTooltipId}
                            setOpenTooltipId={setOpenTooltipId}
                            iconSize={16}
                          />
                        </MDBox>
                      </Grid>
                    )}

                    {props.values.isExtensionRate && (
                      <Grid item xs={12} md={6}>
                        <MDBox display="flex" alignItems="center" ml={-1}>
                          <Checkbox
                            id="isExtensionBasedRate"
                            name="isExtensionBasedRate"
                            checked={props.values.isExtensionBasedRate}
                            onChange={(e) => {
                              props.setFieldValue(
                                "isExtensionBasedRate",
                                e.target.checked,
                              );
                            }}
                            inputProps={{
                              "aria-label": "isExtensionBasedRate",
                            }}
                          />
                          <MDTypography
                            variant="button"
                            fontWeight="regular"
                            color="text"
                          >
                            Is extension based on rate?
                          </MDTypography>
                          <CustomTooltip
                            id="isExtensionBasedRate"
                            title={tabInfo.isExtensionBasedRate}
                            openTooltipId={openTooltipId}
                            setOpenTooltipId={setOpenTooltipId}
                            iconSize={16}
                          />
                        </MDBox>
                      </Grid>
                    )}

                    <Grid item xs={12} md={12} lg={12}>
                      <MDBox display="flex" alignItems="center" ml={-1}>
                        <Checkbox
                          id="isHideFromSuggestions"
                          name="isHideFromSuggestions"
                          checked={props.values.isHideFromSuggestions}
                          onChange={(e) => {
                            props.setFieldValue(
                              "isHideFromSuggestions",
                              e.target.checked,
                            );
                          }}
                          inputProps={{ "aria-label": "isHideFromSuggestions" }}
                        />
                        <MDTypography
                          variant="button"
                          fontWeight="regular"
                          color="text"
                        >
                          Hide this from suggestion chips.
                        </MDTypography>

                        <CustomTooltip
                          id="isHideFromSuggestions"
                          title={tabInfo.isHideFromSuggestions}
                          openTooltipId={openTooltipId}
                          setOpenTooltipId={setOpenTooltipId}
                          iconSize={16}
                        />
                      </MDBox>
                    </Grid>

                    <Grid item xs={12} className="text-right">
                      <MDButton
                        color={sidenavColor}
                        variant="contained"
                        type="submit"
                      >
                        Create
                      </MDButton>
                    </Grid>
                  </Grid>
                </form>
              );
            }}
          </Formik>
        </DialogContent>
      </MDDialog>
    </>
  );
};

export default AssignRatesToPlace;
